"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ShoppingBag,
  CreditCard,
  Wallet,
  TicketPercent,
  CheckCircle2,
  Clock,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useNavigate } from "react-router-dom";
import { CheckoutSkeleton } from "@/components/skeletons";
import { SEO } from "@/components/seo";
import { useCart } from "@/context/CartContext";
import {
  useCreateOrderMutation,
  useInitializePaymentMutation,
  useGetPaymentProvidersQuery,
} from "@/store";
import { toast } from "sonner";
import type { CreateOrderData } from "@/types/order";
import {
  calculateTax,
  calculateTotal,
  isTaxFree,
} from "@/constants/pricing";
import { usePricingSettings } from "@/hooks/usePricingSettings";

type PaymentMethod = "paystack" | "flutterwave";

export default function CheckoutScreen() {
  const navigate = useNavigate();
  const { cartItems, getTotalPrice, appliedCoupon } = useCart();
  const [createOrder, { isLoading: isCreatingOrder }] =
    useCreateOrderMutation();
  const [initializePayment, { isLoading: isInitializingPayment }] =
    useInitializePaymentMutation();
  const {
    taxRate,
    isLoading: isLoadingPricing,
  } = usePricingSettings();
  const { data: paymentProvidersData, isLoading: isLoadingProviders } =
    useGetPaymentProvidersQuery();
  const availableProviders = paymentProvidersData?.data?.providers || [];
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set default payment method based on available providers
  const defaultPaymentMethod =
    availableProviders.length > 0
      ? (availableProviders[0].id as PaymentMethod)
      : "paystack";
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(defaultPaymentMethod);

  // Update payment method when providers load
  useEffect(() => {
    if (
      availableProviders.length > 0 &&
      !availableProviders.find((p) => p.id === paymentMethod)
    ) {
      setPaymentMethod(availableProviders[0].id as PaymentMethod);
    }
  }, [availableProviders, paymentMethod]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
  });

  const currentDate = new Date();
  const currentHour = currentDate.getHours();
  const currentDay = currentDate.getDay(); // 0 is Sunday, 6 is Saturday
  const isPast2PM = currentHour >= 14;

  const sameDayText = isPast2PM
    ? "Closed (after 2pm)"
    : currentDay === 0
      ? "Today from 2 PM"
      : "Today";

  const nextDayText =
    currentDay === 6 ? "Tomorrow from 2 PM" : "Tomorrow from 11 AM";

  const [deliveryTiming, setDeliveryTiming] = useState<
    "same-day" | "next-day" | "scheduled"
  >(isPast2PM ? "next-day" : "same-day");
  const [scheduledDate, setScheduledDate] = useState<string>("");

  useEffect(() => {
    // Redirect to cart if cart is empty
    if (cartItems.length === 0 && !isLoading) {
      navigate("/cart");
    }
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [cartItems.length, navigate, isLoading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePaymentError = (error: any) => {
    console.error("Payment error:", error);
    setIsSubmitting(false);

    // Extract error message from RTK Query error structure
    let errorMessage = "Failed to initialize payment";

    if (error?.data?.error) {
      errorMessage = error.data.error;
    } else if (error?.data?.message) {
      errorMessage = error.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    }

    toast.error(`Payment failed: ${errorMessage}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setIsSubmitting(true);

    try {
      // Build order data
      const orderData: CreateOrderData = {
        userInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phoneNumber,
        },
        items: cartItems.map((item) => ({
          productId: item.productId || String(item.id),
          quantity: item.quantity,
          variations: item.variations,
        })),
        deliveryAddress: {
          label: "Home",
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.postalCode,
          country: "Nigeria",
        },
        paymentMethod: "ONLINE",
        couponCode: appliedCoupon?.code, // Pass the applied coupon code
      };

      // Create order in backend first
      const orderResult = await createOrder(orderData).unwrap();

      // Initialize payment via backend - returns redirect URL
      const paymentResult = await initializePayment({
        orderId: orderResult.data.id,
        provider: paymentMethod,
      }).unwrap();

      // Store cart info for clearing after successful payment
      sessionStorage.setItem("pendingOrderCart", "true");

      // Redirect to payment provider
      window.location.href = paymentResult.data.redirectUrl;
    } catch (error) {
      handlePaymentError(error as Error);
    }
  };

  const subtotal = getTotalPrice();
  const effectiveDeliveryFee = 0; // Disabled: calculated by dispatch
  const discount = appliedCoupon?.discountAmount || 0;

  const tax = calculateTax(subtotal, taxRate);
  const total =
    calculateTotal(subtotal, taxRate, effectiveDeliveryFee) - discount;

  if (isLoading || isLoadingPricing || isLoadingProviders) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">Checkout</h1>
          <p className="text-muted-foreground">Loading checkout form...</p>
        </motion.div>
        <CheckoutSkeleton />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO
        title="Checkout"
        description="Complete your order securely"
        noindex={true}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <Button
          variant="ghost"
          onClick={() => navigate("/cart")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Cart
        </Button>
        <h1 className="text-4xl font-bold mb-2">Checkout</h1>
        <p className="text-muted-foreground">Complete your order securely</p>
      </motion.div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    We'll use this to contact you about your order
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phoneNumber">Phone Number</Label>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      placeholder="08012345678"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Delivery Timing */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mb-6"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Timing</CardTitle>
                  <CardDescription>
                    When would you like your order delivered?
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={deliveryTiming}
                    onValueChange={(val: any) => setDeliveryTiming(val)}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* Same-Day */}
                      <div>
                        <RadioGroupItem
                          value="same-day"
                          id="same-day"
                          disabled={isPast2PM}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="same-day"
                          className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all h-full ${
                            isPast2PM
                              ? "opacity-50 cursor-not-allowed border-muted bg-muted/10 text-muted-foreground"
                              : "border-muted hover:bg-muted/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-[#fcf5f6] peer-data-[state=checked]:text-primary"
                          }`}
                        >
                          <Clock
                            className={`h-6 w-6 mb-2 ${isPast2PM ? "text-muted-foreground" : "text-primary/70"}`}
                          />
                          <span
                            className={`font-semibold text-base mb-1 ${!isPast2PM ? "text-[#333]" : ""}`}
                          >
                            Same-Day
                          </span>
                          <span className="text-xs text-center text-muted-foreground">
                            {sameDayText}
                          </span>
                        </Label>
                      </div>

                      {/* Next-Day */}
                      <div>
                        <RadioGroupItem
                          value="next-day"
                          id="next-day"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="next-day"
                          className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-muted bg-transparent cursor-pointer transition-all hover:bg-muted/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-[#fcf5f6] h-full"
                        >
                          <CalendarDays className="h-6 w-6 mb-2 text-primary/80" />
                          <span className="font-semibold text-base mb-1 text-[#333]">
                            Next-Day
                          </span>
                          <span className="text-xs text-center text-muted-foreground">
                            {nextDayText}
                          </span>
                        </Label>
                      </div>

                      {/* Scheduled */}
                      <div>
                        <RadioGroupItem
                          value="scheduled"
                          id="scheduled"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="scheduled"
                          className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-muted bg-transparent cursor-pointer transition-all hover:bg-muted/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-[#fcf5f6] h-full"
                        >
                          <CalendarDays className="h-6 w-6 mb-2 text-primary/80" />
                          <span className="font-semibold text-base mb-1 text-[#333]">
                            Scheduled
                          </span>
                          <span className="text-xs text-center text-muted-foreground">
                            Pick a date
                          </span>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>

                  {/* Scheduled Date Picker below */}
                  {deliveryTiming === "scheduled" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-6 pt-4 border-t"
                    >
                      <Label
                        htmlFor="scheduled-date"
                        className="mb-2 block font-medium"
                      >
                        Select your delivery date
                      </Label>
                      <Input
                        id="scheduled-date"
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={
                          new Date(Date.now() + 2 * 86400000)
                            .toISOString()
                            .split("T")[0]
                        }
                        required={deliveryTiming === "scheduled"}
                        className="max-w-xs"
                      />
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Delivery Address */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Address</CardTitle>
                  <CardDescription>
                    Where should we deliver your order?
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="123 Main Street"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        placeholder="Lagos"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        type="text"
                        placeholder="Lagos State"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      name="postalCode"
                      type="text"
                      placeholder="100001"
                      value={formData.postalCode}
                      onChange={handleChange}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method</CardTitle>
                  <CardDescription>
                    Choose how you'd like to pay for your order
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={(value) =>
                      setPaymentMethod(value as PaymentMethod)
                    }
                  >
                    {/* Dynamic payment providers from API */}
                    {availableProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-accent cursor-pointer"
                      >
                        <RadioGroupItem value={provider.id} id={provider.id} />
                        <Label
                          htmlFor={provider.id}
                          className="flex-1 cursor-pointer flex items-center gap-2"
                        >
                          {provider.id === "paystack" ? (
                            <CreditCard className="h-5 w-5" />
                          ) : provider.id === "flutterwave" ? (
                            <Wallet className="h-5 w-5" />
                          ) : null}
                          <div>
                            <div className="font-semibold">{provider.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {provider.description}
                            </div>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            </motion.div>

            {/* Order Items */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    Order Items
                  </CardTitle>
                  <CardDescription>
                    Review your order before proceeding
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-card shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h4 className="font-semibold">{item.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Quantity: {item.quantity} × ₦
                              {item.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-1"
          >
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
                <CardDescription>Review your order</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>₦{subtotal.toLocaleString()}</span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <div className="flex items-center gap-1.5 text-green-600 font-medium">
                        <TicketPercent className="h-3.5 w-3.5" />
                        <span>Discount ({appliedCoupon?.code})</span>
                      </div>
                      <span className="text-green-600 font-semibold">
                        -₦{discount.toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span
                      className={
                        isTaxFree(taxRate) ? "text-green-600 font-medium" : ""
                      }
                    >
                      {isTaxFree(taxRate) ? "Free" : `₦${tax.toLocaleString()}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Delivery Fee</span>
                    {appliedCoupon?.isFreeDelivery ? (
                      <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-tight">
                        <CheckCircle2 className="h-3 w-3" />
                        Free Delivery
                      </div>
                    ) : (
                      <span className="text-xs italic text-muted-foreground">
                        calculated to dispatch not us
                      </span>
                    )}
                  </div>

                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={total}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-primary"
                      >
                        ₦{total.toLocaleString()}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button
                  type="submit"
                  className="w-full text-base font-bold"
                  size="lg"
                  disabled={
                    isSubmitting ||
                    isCreatingOrder ||
                    isInitializingPayment ||
                    cartItems.length === 0
                  }
                >
                  {paymentMethod === "paystack" ? (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {isSubmitting ? "Processing..." : "Pay with Paystack"}
                    </>
                  ) : (
                    <>
                      <Wallet className="mr-2 h-4 w-4" />
                      {isSubmitting ? "Processing..." : "Pay with Flutterwave"}
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  You will be redirected to complete your payment securely
                </p>
              </CardFooter>
            </Card>
          </motion.div>
        </div>
      </form>
    </div>
  );
}
